import { cn } from '../../lib/cn.js';

export type BadgeVariant =
  | 'new'
  | 'sale'
  | 'sold'
  | 'rent'
  | 'rented'
  | 'potential'
  | 'unavailable'
  | 'no_sale'
  | 'no_answer'
  | 'wrong_number'
  | 'agent'
  | 'call'
  | 'whatsapp'
  | 'note'
  | 'stage_change'
  | 'assignment'
  | 'edit'
  | 'admin'
  | 'super_admin'
  | 'manager'
  | 'user'
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger';

const STAGE_LABELS: Record<string, string> = {
  new: 'New',
  sale: 'Sale',
  sold: 'Sold',
  rent: 'Rent',
  rented: 'Rented',
  potential: 'Potential',
  unavailable: 'Unavailable',
  no_sale: 'No Sale',
  no_answer: 'No Answer',
  wrong_number: 'Wrong Number',
  agent: 'Agent',
};

// Sherzod-specified palette 2026-05-03:
//   new=blue · sale=green · sold=red · rent=orange · rented=crimson
//   potential=purple · unavailable=gray · no_sale=red · no_answer=yellow
//   wrong_number=red · agent=light blue
const STAGE_STYLES: Record<string, string> = {
  new:          'bg-blue-100 text-blue-800',
  sale:         'bg-green-100 text-green-800',
  sold:         'bg-red-100 text-red-800',
  rent:         'bg-orange-100 text-orange-800',
  rented:       'bg-rose-200 text-rose-900',
  potential:    'bg-purple-100 text-purple-800',
  unavailable:  'bg-gray-100 text-gray-700',
  no_sale:      'bg-red-100 text-red-800',
  no_answer:    'bg-yellow-100 text-yellow-800',
  wrong_number: 'bg-red-100 text-red-800',
  agent:        'bg-sky-100 text-sky-800',
};

const ROLE_STYLES: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  manager: 'bg-yellow-100 text-yellow-800',
  user: 'bg-gray-100 text-gray-700',
};

const ACTIVITY_STYLES: Record<string, string> = {
  call: 'bg-green-100 text-green-800',
  whatsapp: 'bg-emerald-100 text-emerald-800',
  note: 'bg-blue-100 text-blue-800',
  stage_change: 'bg-orange-100 text-orange-800',
  assignment: 'bg-purple-100 text-purple-800',
  edit: 'bg-gray-100 text-gray-700',
  import: 'bg-gray-100 text-gray-600',
};

interface BadgeProps {
  variant?: BadgeVariant;
  children: React.ReactNode;
  className?: string;
}

export function Badge({ variant = 'neutral', children, className }: BadgeProps) {
  const style =
    STAGE_STYLES[variant] ??
    ROLE_STYLES[variant] ??
    ACTIVITY_STYLES[variant] ??
    'bg-gray-100 text-gray-700';

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        style,
        className
      )}
    >
      {children}
    </span>
  );
}

export function StageBadge({ stage }: { stage: string }) {
  return (
    <Badge variant={stage as BadgeVariant}>
      {STAGE_LABELS[stage] ?? stage}
    </Badge>
  );
}

export function RoleBadge({ role }: { role: string }) {
  return (
    <Badge variant={role as BadgeVariant}>
      {role.replace('_', ' ')}
    </Badge>
  );
}

export function ActivityTypeBadge({ type }: { type: string }) {
  const labels: Record<string, string> = {
    call: 'Call',
    whatsapp: 'WhatsApp',
    note: 'Note',
    stage_change: 'Stage',
    assignment: 'Assignment',
    edit: 'Edit',
    import: 'Import',
  };
  return (
    <Badge variant={type as BadgeVariant}>
      {labels[type] ?? type}
    </Badge>
  );
}
